import { memo, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Header } from "./Header";
import type { Screen } from "../types";

interface FAQPageProps {
  onBack: () => void;
  onNavigate?: (screen: Screen) => void;
}

export const FAQPage = memo(function FAQPage({ onBack, onNavigate }: FAQPageProps) {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: t("faq.q1"),
      answer: t("faq.a1"),
    },
    {
      question: t("faq.q2"),
      answer: t("faq.a2"),
    },
    {
      question: t("faq.q3"),
      answer: t("faq.a3"),
    },
    {
      question: t("faq.q4"),
      answer: t("faq.a4"),
    },
    {
      question: t("faq.q5"),
      answer: t("faq.a5"),
    },
    {
      question: t("faq.q6"),
      answer: t("faq.a6"),
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      <Header onNavigate={onNavigate} showNav={false} />
      
      <main className="max-w-md mx-auto w-full px-4 py-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-4 p-2 hover:bg-white/80 rounded-xl transition-smooth inline-flex items-center gap-2"
          aria-label="Back"
        >
          <ArrowLeft
            className={`w-5 h-5 text-slate-600 ${
              language === "ar" ? "rotate-180" : ""
            }`}
          />
          <span className="text-slate-600 font-medium">{t("common.back")}</span>
        </button>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-4">
          <h1 className="text-slate-900 font-semibold text-xl mb-6">
            {t("faq.title")}
          </h1>
          
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-slate-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 transition-smooth"
              >
                <span className="font-semibold text-slate-900 pr-4">
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-slate-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-4 pb-4">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}

          {/* Developer Credit */}
          <div className="border-t border-slate-200 pt-6 mt-8">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 text-center space-y-3">
              <p className="text-sm text-slate-600">
                {t("faq.developedBy")}{" "}
                <a
                  href="https://rekcode.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark font-bold transition-smooth"
                >
                  rekcode.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
});

